import React from 'react';
import { FaDirections, FaEye, FaMapMarkerAlt, FaBed, FaUtensils, FaStar, FaExchangeAlt, FaSpinner, FaLock, FaTimes, FaChevronDown } from 'react-icons/fa';
import BookingOptions from '../BookingOptions';
import SwapDayPicker from './SwapDayPicker';
import { formatDayDate, getDayDate, getAttractionStatus } from './dashboardUtils';
import {
  usePlaceImage,
  isDrivingTrip as isDriving,
  nightWindowForDay,
  dayHasAccommodation,
} from '@travel-planner/shared';

// Pattern to detect meal-related locations
const MEAL_PATTERN = /\b(breakfast|lunch|dinner|brunch|meal|restaurant|cafe|bistro|eatery|diner|food|dining)\b/i;

/** Small place image for the day card stop list with placeholder fallback. */
function DayCardStopImage({ name, lat, lng }) {
  const imageUrl = usePlaceImage(name, lat, lng);
  const [hasError, setHasError] = React.useState(false);

  // Reset error state when URL changes
  React.useEffect(() => {
    setHasError(false);
  }, [imageUrl]);

  // Show placeholder with icon when no image is available or on error
  if (!imageUrl || hasError) {
    return (
      <div className="w-10 h-10 rounded-md flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <FaMapMarkerAlt className="text-slate-400 text-sm" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={name}
      loading="lazy"
      className="w-10 h-10 rounded-md object-cover flex-shrink-0"
      onError={() => setHasError(true)}
    />
  );
}

/**
 * DayCard - Individual day card with warm, colorful design.
 */
const DayCard = ({
  day,
  startDate,
  weather,
  attractionStatuses,
  isCurrentDay,
  onViewDay,
  onSwapDays,
  allDays = [],
  isSwapping,
  isLocked,
  destination,
  onRemoveStop,
  isOwner = true,
  isExpanded = true,
  onToggleExpand,
  // Base CJ sub-ID for this itinerary — "itin_1841" for a real trip,
  // "sample_tokyo-japan" for a public sample. The day number is appended, so a
  // click can be traced to the exact card that earned it.
  bookingSurface,
  endDate,
  transportationMode,
}) => {
  const [showSwapPicker, setShowSwapPicker] = React.useState(false);
  const { day: dayNumber, dayTitle, locations = [] } = day;
  const dateStr = getDayDate(startDate, dayNumber);
  const isDrivingTrip = isDriving(transportationMode);
  const formattedDate = formatDayDate(dateStr);
  // The night this day covers, and the city to search — shared by the per-stop
  // buttons and the fallback below so they cannot disagree.
  const nightWindow = nightWindowForDay(startDate, dayNumber);
  const stayCity =
    (dayTitle?.includes(':') ? dayTitle.split(':').slice(1).join(':').trim() : '') || destination;

  // Extract location name from dayTitle
  const locationName = dayTitle?.includes(':')
    ? dayTitle.split(':').slice(1).join(':').trim()
    : dayTitle || `Day ${dayNumber}`;

  // Calculate stats
  let visitedCount = 0;
  locations.forEach((loc) => {
    const status = getAttractionStatus(loc, dayNumber, attractionStatuses);
    if (status === 'visited') visitedCount++;
  });
  const totalStops = locations.length;
  const completionPercent = totalStops > 0 ? Math.round((visitedCount / totalStops) * 100) : 0;

  // Generate Google Maps directions URL
  const getDirectionsUrl = () => {
    if (locations.length < 1) return null;

    const validLocs = locations.filter(l => l.lat && l.lng);
    if (validLocs.length === 0) return null;

    if (validLocs.length === 1) {
      return `https://www.google.com/maps/dir/?api=1&destination=${validLocs[0].lat},${validLocs[0].lng}`;
    }

    const origin = `${validLocs[0].lat},${validLocs[0].lng}`;
    const destination = `${validLocs[validLocs.length - 1].lat},${validLocs[validLocs.length - 1].lng}`;
    const waypoints = validLocs
      .slice(1, -1)
      .map((loc) => `${loc.lat},${loc.lng}`)
      .join('|');

    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (waypoints) {
      url += `&waypoints=${waypoints}`;
    }
    return url;
  };

  const directionsUrl = getDirectionsUrl();

  return (
    <div
      onClick={isLocked ? onViewDay : undefined}
      className={`
        relative bg-white rounded-xl border-2 transition-all duration-200 overflow-hidden flex flex-col h-full
        ${isLocked
          ? 'border-slate-200 shadow-sm opacity-60 grayscale cursor-pointer hover:opacity-70'
          : isCurrentDay
            ? 'border-amber-400 shadow-md ring-2 ring-amber-100 hover:shadow-lg'
            : 'border-slate-200 shadow-sm hover:border-sky-300 hover:shadow-lg'
        }
      `}
    >
      {/* Lock overlay for free/demo users */}
      {isLocked && (
        <div className="absolute inset-0 bg-slate-900/20 z-10 flex items-center justify-center rounded-xl">
          <div className="bg-white/90 rounded-full p-3 shadow-lg">
            <FaLock className="text-slate-500 text-xl" />
          </div>
        </div>
      )}
      {/* Swap spinner overlay */}
      {isSwapping && (
        <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-xl">
          <FaSpinner className="animate-spin text-sky-500 text-xl" />
        </div>
      )}
      {/* Header with gradient — clickable to collapse/expand */}
      <div
        onClick={!isLocked && onToggleExpand ? (e) => { e.stopPropagation(); onToggleExpand(); } : undefined}
        className={`px-4 py-3 ${!isLocked && onToggleExpand ? 'cursor-pointer select-none' : ''} ${isCurrentDay ? 'bg-gradient-to-r from-amber-50 to-orange-50' : 'bg-gradient-to-r from-sky-50 to-indigo-50'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`
              px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm
              ${isCurrentDay ? 'bg-amber-500 text-white' : 'bg-sky-500 text-white'}
            `}>
              Day {dayNumber}
            </span>
            {isCurrentDay && (
              <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">Today</span>
            )}
            {!isExpanded && totalStops > 0 && (
              <span className="text-xs text-slate-500">
                {totalStops} {totalStops === 1 ? 'stop' : 'stops'}
              </span>
            )}
          </div>

          {/* Swap button — opens picker to choose target day */}
          {onSwapDays && allDays.length > 1 && (
            <div className="ml-auto mr-2">
              <button
                onClick={(e) => { e.stopPropagation(); setShowSwapPicker(true); }}
                disabled={isSwapping}
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/60 hover:bg-white text-slate-500 hover:text-sky-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                title={`Swap Day ${dayNumber} with another day`}
              >
                <FaExchangeAlt size={10} />
                <span>Swap</span>
              </button>
            </div>
          )}

          {/* Weather */}
          {weather && !weather.skip_reason && (
            <div
              className="flex items-center gap-1.5 text-sm bg-white/80 px-2 py-1 rounded-lg shadow-sm"
              title={weather.is_seasonal_estimate
                ? 'Typical weather for this time of year (historical average). A live forecast appears within ~16 days of your trip.'
                : weather.description || ''}
            >
              <span className="text-lg">{weather.emoji}</span>
              <span className="font-semibold text-slate-700">
                {weather.temp_max_f ? `${Math.round(weather.temp_max_f)}°` : ''}
              </span>
              {weather.is_seasonal_estimate && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                  ~typ
                </span>
              )}
            </div>
          )}

          {/* Expand/collapse chevron */}
          {!isLocked && onToggleExpand && (
            <FaChevronDown
              className={`ml-2 text-slate-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              size={14}
              aria-label={isExpanded ? 'Collapse day' : 'Expand day'}
            />
          )}
        </div>

        <p className="text-xs text-slate-500 mt-2 font-medium">{formattedDate}</p>
        <h3 className="font-bold text-slate-800 mt-0.5 truncate text-lg" title={locationName}>
          {locationName}
        </h3>
      </div>

      {/* Body — hidden when collapsed */}
      {isExpanded && (
      <div className="p-4 flex-1 flex flex-col">
        {/* Stops List */}
        <div className="space-y-2.5 mb-4 flex-1">
          {locations.length > 0 ? (
            <>
              {locations.slice(0, 3).map((loc, idx) => {
                const status = getAttractionStatus(loc, dayNumber, attractionStatuses);
                return (
                  <div
                    key={idx}
                    className={`flex items-start gap-3 text-sm ${loc.isAccommodation ? 'bg-purple-50 -mx-2 px-2 py-1.5 rounded-lg' : ''}`}
                  >
                    <DayCardStopImage name={loc.name} lat={loc.lat} lng={loc.lng} />
                    <span className={`
                      w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-sm flex-shrink-0 mt-0.5
                      ${MEAL_PATTERN.test(loc.name) ? 'bg-orange-500 text-white' : loc.isAccommodation ? 'bg-purple-500 text-white' : status === 'visited' ? 'bg-emerald-500 text-white' : 'bg-indigo-500 text-white'}
                    `}>
                      {MEAL_PATTERN.test(loc.name) ? <FaUtensils size={10} /> : loc.isAccommodation ? <FaBed size={10} /> : idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`
                            truncate font-medium
                            ${status === 'visited' ? 'line-through text-slate-400' : 'text-slate-700'}
                          `}
                          title={loc.name}
                        >
                          {loc.name}
                        </span>
                        {loc.starRating && (
                          <span className="flex items-center text-amber-500">
                            {[...Array(Math.min(loc.starRating, 5))].map((_, i) => (
                              <FaStar key={i} size={8} />
                            ))}
                          </span>
                        )}
                      </div>
                      {loc.lat && loc.lng && (
                        <span className="text-xs text-slate-400 font-mono block">
                          {loc.lat.toFixed(4)}, {loc.lng.toFixed(4)}
                        </span>
                      )}
                      {/* Gated on the parser's flag alone. It used to also
                          require loc.bookingLink, which mobile never did — so
                          the same trip showed a hotel button on Android and
                          nothing on the web. The parser's promotion rules
                          (booking link, or star rating plus name) already make
                          the flag the more reliable signal of the two. */}
                      {loc.isAccommodation && (
                        <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                          <BookingOptions
                            type="hotel"
                            hotelName={loc.name}
                            city={stayCity}
                            checkIn={nightWindow.checkIn}
                            checkOut={nightWindow.checkOut}
                            compact={true}
                            surface={bookingSurface ? `${bookingSurface}_day${dayNumber}` : undefined}
                          />
                        </div>
                      )}
                    </div>
                    {/* Remove stop button */}
                    {isOwner && onRemoveStop && !isLocked && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveStop(dayNumber, loc.name, loc.lat, loc.lng);
                        }}
                        className="flex-shrink-0 p-1.5 bg-red-100 text-red-500 hover:bg-red-500 hover:text-white rounded-full transition-colors shadow-sm"
                        title={`Remove ${loc.name}`}
                      >
                        <FaTimes size={10} />
                      </button>
                    )}
                  </div>
                );
              })}
              {locations.length > 3 && (
                <p className="text-xs text-indigo-500 font-medium pl-9">
                  +{locations.length - 3} more stops
                </p>
              )}
            </>
          ) : (
            <div className="text-center py-4 text-slate-400 text-sm">
              <FaMapMarkerAlt className="mx-auto mb-1 text-lg text-slate-300" />
              No stops planned
            </div>
          )}
        </div>

        {/* Where to stay on Day N — the fallback for a day the AI never named
            anywhere to sleep. Without it those days carry no stay surface at
            all, which is both a gap in the plan and the single most common
            question the traveller still has about that day. It is deliberately
            an area search rather than a named property: we are not pretending
            to recommend somewhere, only offering to answer the question. */}
        {!isLocked && stayCity && !dayHasAccommodation(locations) && (
          <div className="mb-4 pt-3 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-2">
              Where to stay on Day {dayNumber}
            </p>
            <div onClick={(e) => e.stopPropagation()}>
              <BookingOptions
                type="hotel"
                city={stayCity}
                checkIn={nightWindow.checkIn}
                checkOut={nightWindow.checkOut}
                compact={true}
                surface={bookingSurface ? `${bookingSurface}_day${dayNumber}_stay` : undefined}
              />
            </div>
          </div>
        )}

        {/* Getting around — Day 1 only. The ride in from the airport is the
            first decision a traveller makes on arrival, and a car is only worth
            offering once the trip itself says they're driving. */}
        {dayNumber === 1 && destination && !isLocked && (
          <div className="mb-4 pt-3 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-2">Getting around</p>
            <div className="flex items-center gap-2 flex-wrap">
              <BookingOptions
                type="transfer"
                city={destination}
                checkIn={dateStr}
                compact={true}
                surface={bookingSurface ? `${bookingSurface}_day1_transfer` : undefined}
              />
              {isDrivingTrip && (
                <BookingOptions
                  type="car"
                  city={destination}
                  checkIn={dateStr}
                  checkOut={endDate}
                  compact={true}
                  surface={bookingSurface ? `${bookingSurface}_car` : undefined}
                />
              )}
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {totalStops > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-slate-500 font-medium">Progress</span>
              <span className="text-emerald-600 font-bold">{visitedCount}/{totalStops}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all duration-300 rounded-full"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-auto">
          <button
            onClick={onViewDay}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500 text-white text-sm font-semibold rounded-lg transition-all shadow-sm hover:shadow"
          >
            <FaEye className="text-xs" />
            View Day
          </button>
          {directionsUrl && (
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center p-2.5 bg-sky-100 hover:bg-sky-200 text-sky-600 rounded-lg transition-colors"
              title="Get directions"
            >
              <FaDirections />
            </a>
          )}
        </div>
      </div>
      )}

      {/* Swap target picker */}
      <SwapDayPicker
        isOpen={showSwapPicker}
        onClose={() => setShowSwapPicker(false)}
        sourceDay={day}
        allDays={allDays}
        startDate={startDate}
        onSelect={(targetDayNumber) => {
          if (onSwapDays && targetDayNumber && targetDayNumber !== dayNumber) {
            onSwapDays(dayNumber, targetDayNumber);
          }
        }}
      />
    </div>
  );
};

export default DayCard;
